import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { ANALYTICS_GRAPH_TYPES } from './graphCatalog';

const WORLD_ATLAS_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';
const D3_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/d3@7/dist/d3.min.js';
const TOPOJSON_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/topojson-client@3/dist/topojson-client.min.js';

const normalize = (value) => (value || '').toLowerCase().replace(/[^a-z\u0600-\u06ff]/g, '');

const COUNTRY_FLAGS = {
  lebanon: '🇱🇧',
  jordan: '🇯🇴',
  egypt: '🇪🇬',
  saudi_arabia: '🇸🇦',
  uae: '🇦🇪'
};

const COUNTRY_NAME_ALIASES = {
  uae: ['united arab emirates'],
  usa: ['united states of america', 'united states'],
  united_states: ['united states of america', 'united states'],
  uk: ['united kingdom'],
  united_kingdom: ['united kingdom']
};

const STANDARD_REGION_FEATURE_NAMES = {
  arab_region: [
    'algeria', 'bahrain', 'comoros', 'djibouti', 'egypt', 'iraq', 'jordan', 'kuwait',
    'lebanon', 'libya', 'mauritania', 'morocco', 'oman', 'palestine', 'qatar',
    'saudi arabia', 'somalia', 'sudan', 'syria', 'tunisia', 'united arab emirates', 'yemen'
  ],
  americas: [
    'argentina', 'bolivia', 'brazil', 'canada', 'chile', 'colombia', 'costa rica',
    'cuba', 'dominican republic', 'ecuador', 'el salvador', 'guatemala', 'haiti',
    'honduras', 'jamaica', 'mexico', 'nicaragua', 'panama', 'paraguay', 'peru',
    'united states of america', 'uruguay', 'venezuela'
  ],
  asia: [
    'china', 'india', 'indonesia', 'japan', 'malaysia', 'pakistan', 'philippines',
    'singapore', 'south korea', 'sri lanka', 'thailand', 'vietnam'
  ],
  emea: [
    'austria', 'belgium', 'czechia', 'denmark', 'finland', 'france', 'germany',
    'greece', 'ireland', 'italy', 'netherlands', 'norway', 'poland', 'portugal',
    'romania', 'south africa', 'spain', 'sweden', 'switzerland', 'turkey',
    'united kingdom'
  ]
};

const namesForCountry = (country) => {
  const id = String(country?.id || '');
  const label = String(country?.label || '');
  const normalizedIdLabel = id.replace(/_/g, ' ');
  return [label, normalizedIdLabel, ...(COUNTRY_NAME_ALIASES[id] || [])].filter(Boolean);
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const loadScript = (src, key) =>
  new Promise((resolve, reject) => {
    if (window[key]) {
      resolve();
      return;
    }

    const existing = document.querySelector(`script[data-analytics-script="${key}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.analyticsScript = key;
    script.onload = () => {
      window[key] = true;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

const GeoTrafficVisual = ({ model, isRTL = false, onChartTypeChange }) => {
  useUiLanguage();
  const { size = 'large', activeGraph, chartType = activeGraph || ANALYTICS_GRAPH_TYPES.WORLD_CHOROPLETH, dataset, options = {} } = model;
  const countries = useMemo(() => dataset?.countries || [], [dataset]);
  const dynamicRegions = useMemo(() => dataset?.regions || [], [dataset]);
  const copy = options.copy || {};
  const [view, setView] = useState(size === 'compact' ? 'world' : 'world');
  const [selectedRegion, setSelectedRegion] = useState(dataset?.defaultRegionId || dynamicRegions[0]?.id || '');
  const [selectedCountry, setSelectedCountry] = useState(dataset?.defaultCountryId || countries[0]?.id || '');
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        await Promise.all([
          loadScript(D3_SCRIPT_URL, '__analyticsD3Loaded'),
          loadScript(TOPOJSON_SCRIPT_URL, '__analyticsTopoLoaded')
        ]);
        const response = await fetch(WORLD_ATLAS_URL);
        const topology = await response.json();
        const loadedFeatures = window.topojson.feature(topology, topology.objects.countries).features;
        if (!cancelled) {
          setFeatures(loadedFeatures);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (size === 'compact') {
      setView('world');
    }
  }, [size]);

  useEffect(() => {
    if (chartType === ANALYTICS_GRAPH_TYPES.WORLD_CHOROPLETH) {
      setView('world');
    } else if (chartType === ANALYTICS_GRAPH_TYPES.REGIONAL_MAP) {
      setView('region');
    } else if (chartType === ANALYTICS_GRAPH_TYPES.CITY_DOT_MAP) {
      setView('country');
    }
  }, [chartType]);

  useEffect(() => {
    if (!countries.length) return;
    if (!countries.some((country) => country.id === selectedCountry)) {
      setSelectedCountry(countries[0].id);
    }
  }, [countries, selectedCountry]);

  useEffect(() => {
    if (!dynamicRegions.length) return;
    if (!dynamicRegions.some((region) => region.id === selectedRegion)) {
      setSelectedRegion(dynamicRegions[0].id);
    }
  }, [dynamicRegions, selectedRegion]);

  const regionRows = useMemo(
    () =>
      dynamicRegions.map((region) => {
        const matched = countries.filter((country) => country.regionId === region.id);
        const volume = matched.reduce((sum, country) => sum + country.applicationVolume, 0);
        const conversionRate = matched.length
          ? matched.reduce((sum, country) => sum + country.conversionRate, 0) / matched.length
          : 0;
        return {
          id: region.id,
          label: copy.regions?.[region.id] || region.label,
          volume,
          conversionRate: Number(conversionRate.toFixed(1)),
          names: matched.flatMap(namesForCountry)
        };
      }),
    [copy.regions, countries, dynamicRegions]
  );

  const selectedCountryData = useMemo(
    () => countries.find((country) => country.id === selectedCountry) || countries[0],
    [countries, selectedCountry]
  );

  const dataByName = useMemo(
    () => new Map(countries.flatMap((country) => namesForCountry(country).map((name) => [normalize(name), country]))),
    [countries]
  );

  const regionFeatureNames = useMemo(() => {
    const region = regionRows.find((item) => item.id === selectedRegion);
    const standardNames = STANDARD_REGION_FEATURE_NAMES[selectedRegion] || [];
    return region ? [...region.names, ...standardNames].map(normalize) : standardNames.map(normalize);
  }, [regionRows, selectedRegion]);

  const selectedCountryRegionNames = useMemo(() => {
    if (!selectedCountryData) return [];
    const standardNames = STANDARD_REGION_FEATURE_NAMES[selectedCountryData.regionId] || [];
    return [
      ...countries.filter((country) => country.regionId === selectedCountryData.regionId).flatMap(namesForCountry),
      ...standardNames
    ].map(normalize);
  }, [countries, selectedCountryData]);

  const focusFeatures = useMemo(() => {
    if (view === 'region') {
      return features.filter((feature) =>
        regionFeatureNames.includes(normalize(feature?.properties?.name || feature?.properties?.NAME || feature?.id || ''))
      );
    }

    if (view === 'country' && selectedCountryData) {
      return features.filter((feature) =>
        selectedCountryRegionNames.includes(normalize(feature?.properties?.name || feature?.properties?.NAME || feature?.id || ''))
      );
    }

    return features;
  }, [features, regionFeatureNames, selectedCountryData, selectedCountryRegionNames, view]);

  const svgMarkup = useMemo(() => {
    if (!features.length || !window.d3) return null;
    const d3 = window.d3;
    const width = 920;
    const height = size === 'large' ? 440 : size === 'three-quarter' ? 400 : 360;
    const renderFeatures = focusFeatures.length ? focusFeatures : features;
    const collection = { type: 'FeatureCollection', features: renderFeatures };
    const projection =
      view === 'world'
        ? d3.geoNaturalEarth1().fitExtent([[10, 10], [width - 10, height - 10]], collection)
        : d3.geoMercator().fitExtent([[24, 20], [width - 24, height - 20]], collection);
    const path = d3.geoPath(projection);
    const maxVolume = Math.max(...countries.map((country) => country.applicationVolume), 1);

    return {
      width,
      height,
      paths: features.map((feature) => {
        const featureName = feature?.properties?.name || feature?.properties?.NAME || feature?.id || '';
        const matched = dataByName.get(normalize(featureName));
        const isSelectedCountry =
          view === 'country' &&
          selectedCountryData &&
          namesForCountry(selectedCountryData).some((name) => normalize(featureName) === normalize(name));
        const isFocused =
          view === 'world'
            ? Boolean(matched)
            : view === 'region'
              ? regionFeatureNames.includes(normalize(featureName))
              : selectedCountryRegionNames.includes(normalize(featureName));
        const intensity = matched ? 0.25 + (((matched.applicationVolume || 0) / maxVolume) * 0.75) : 0.08;
        return {
          id: feature.id || featureName,
          name: featureName,
          d: path(feature),
          fill:
            !isFocused
              ? 'rgba(var(--analytics-accent-rgb), 0.045)'
              : view === 'country' && !isSelectedCountry
                ? 'rgba(var(--analytics-accent-rgb), 0.14)'
                : matched
                  ? `rgba(var(--analytics-accent-rgb), ${intensity.toFixed(2)})`
                  : 'rgba(var(--analytics-accent-rgb), 0.08)',
          stroke:
            view === 'country' && isSelectedCountry
              ? 'rgba(var(--analytics-accent-rgb), 0.95)'
              : isFocused
                ? 'rgba(255, 255, 255, 0.4)'
                : 'rgba(255, 255, 255, 0.12)',
          strokeWidth: view === 'world' ? 0.8 : isSelectedCountry ? 1.9 : isFocused ? 1.2 : 0.8
        };
      }),
      cityDots:
        view === 'country' && selectedCountryData
          ? (selectedCountryData.cities || [])
              .map((city) => {
                if (!Number.isFinite(Number(city.lon)) || !Number.isFinite(Number(city.lat))) return null;
                const point = projection([city.lon, city.lat]);
                if (!point) return null;
                return { ...city, x: point[0], y: point[1] };
              })
              .filter(Boolean)
          : []
    };
  }, [countries, dataByName, features, focusFeatures, regionFeatureNames, selectedCountryData, selectedCountryRegionNames, size, view]);

  const rightRows = useMemo(() => {
    if (size === 'compact') {
      const maxVolume = Math.max(...countries.map((country) => country.applicationVolume), 1);
      return countries.map((country) => ({
        key: country.id,
        label: country.label,
        flag: COUNTRY_FLAGS[country.id] || '📍',
        a: country.applicationVolume.toLocaleString(),
        b: formatPercent(country.conversionRate),
        width: (country.applicationVolume / maxVolume) * 100,
        onClick: () => setSelectedCountry(country.id)
      }));
    }

    if (view === 'region') {
      const regionCountries = countries.filter((country) => country.regionId === selectedRegion);
      const maxVolume = Math.max(...regionCountries.map((country) => country.applicationVolume), 1);
      return regionCountries.map((country) => ({
        key: country.id,
        label: country.label,
        flag: COUNTRY_FLAGS[country.id] || '📍',
        a: country.applicationVolume.toLocaleString(),
        b: formatPercent(country.conversionRate),
        width: (country.applicationVolume / maxVolume) * 100,
        onClick: () => setSelectedCountry(country.id)
      }));
    }

    if (view === 'world') {
      const maxVolume = Math.max(...countries.map((country) => country.applicationVolume), 1);
      return countries.map((country) => ({
        key: country.id,
        label: country.label,
        flag: COUNTRY_FLAGS[country.id] || '📍',
        a: country.applicationVolume.toLocaleString(),
        b: formatPercent(country.conversionRate),
        width: (country.applicationVolume / maxVolume) * 100,
        onClick: () => setSelectedCountry(country.id)
      }));
    }

    const cityList = selectedCountryData?.cities || [];
    const maxVolume = Math.max(...cityList.map((city) => Number(city.applications ?? city.applicationVolume ?? 0)), 1);
    return cityList.map((city) => {
      const applications = Number(city.applications ?? city.applicationVolume ?? 0);
      const hires = Number(city.hires ?? city.hireCount ?? 0);
      return {
        key: city.id || city.label,
        label: city.label,
        a: applications.toLocaleString(),
        b: `${hires} / ${formatPercent(city.conversionRate)}`,
        width: (applications / maxVolume) * 100,
        onClick: () => undefined
      };
    });
  }, [countries, selectedCountryData, selectedRegion, size, view]);

  return (
    <div className={`analytics-visual analytics-visual-geo ${size}${isRTL ? ' rtl' : ''}`}>
      {size !== 'compact' ? (
        <div className="analytics-visual-geo-toolbar">
          <div className="analytics-recruitment-map-toggle">
            {(copy.views || []).map((item) => (
              <button
                key={item.id}
                type="button"
                className={`analytics-recruitment-map-toggle-button${view === item.id ? ' active' : ''}`}
                onClick={() => {
                  setView(item.id);
                  if (item.id === 'world') onChartTypeChange?.(ANALYTICS_GRAPH_TYPES.WORLD_CHOROPLETH);
                  if (item.id === 'region') onChartTypeChange?.(ANALYTICS_GRAPH_TYPES.REGIONAL_MAP);
                  if (item.id === 'country') onChartTypeChange?.(ANALYTICS_GRAPH_TYPES.CITY_DOT_MAP);
                }}
              >
                {uiText(item.label)}
              </button>
            ))}
          </div>

          {view === 'country' ? (
            <label className="analytics-recruitment-map-country-select">
              <select className="analytics-recruitment-map-country-native" value={selectedCountry} onChange={(event) => setSelectedCountry(event.target.value)}>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {uiText(country.label)}
                  </option>
                ))}
              </select>
              <i className="fa-solid fa-chevron-down analytics-recruitment-filter-icon" />
            </label>
          ) : null}
        </div>
      ) : null}

      {isLoading || hasError || !svgMarkup ? (
        <div className="analytics-recruitment-map-loading">
          <i className={`fa-solid ${hasError ? 'fa-circle-exclamation' : 'fa-spinner fa-spin'}`} />
          <span>{hasError ? copy.error : copy.loading}</span>
        </div>
      ) : (
        <div className="analytics-visual-geo-layout">
          {size !== 'compact' ? (
            <div className="analytics-visual-geo-map">
              <svg viewBox={`0 0 ${svgMarkup.width} ${svgMarkup.height}`} className="analytics-recruitment-map-svg" role="img" aria-label={copy.ariaLabel}>
                {svgMarkup.paths.map((shape) => (
                  <path
                    key={shape.id}
                    d={shape.d}
                    fill={shape.fill}
                    stroke={shape.stroke}
                    strokeWidth={shape.strokeWidth}
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {view === 'country' &&
                  svgMarkup.cityDots.map((city) => (
                    <circle
                      key={city.label}
                      cx={city.x}
                      cy={city.y}
                      r={Math.max(3, Number(city.applications ?? city.applicationVolume ?? 0) / 34)}
                      fill={`rgba(var(--analytics-accent-rgb), ${Math.min(1, 0.2 + (Number(city.conversionRate || 0) / 100) * 0.8)})`}
                      stroke="var(--analytics-surface)"
                      strokeWidth="1.25"
                    />
                  ))}
              </svg>
            </div>
          ) : null}

          <div className={`analytics-visual-geo-side${size === 'compact' ? ' compact' : ''}`}>
            {rightRows.map((row) => (
              <button key={row.key} type="button" className="analytics-visual-geo-side-row" onClick={row.onClick}>
                <div className="analytics-visual-row-head">
                  <span className="analytics-visual-label analytics-visual-geo-side-label">
                    {row.flag ? <span className="analytics-visual-geo-flag">{row.flag}</span> : null}
                    <span>{uiText(row.label)}</span>
                  </span>
                  <div className="analytics-visual-row-metrics">
                    <strong className="analytics-visual-number">{row.a}</strong>
                    <span className="analytics-visual-sub">{row.b}</span>
                  </div>
                </div>
                <div className="analytics-visual-track slim">
                  <div className="analytics-visual-fill accent" style={{ width: `${Math.max(10, row.width)}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeoTrafficVisual;
